import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import ReviewCarousel from '../components/ReviewCarousel';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';
import { fetchBlogPosts, formatPostDate, initialBlogData } from '../lib/blogApi';
import { blogListSchema } from '../lib/schema';

const Blog = () => {
  // Start from the posts built into the page, if any, then refresh from the API.
  const [posts, setPosts] = useState(() => initialBlogData()?.list || []);
  const [loading, setLoading] = useState(() => !initialBlogData()?.list?.length);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBlogPosts()
      .then(setPosts)
      .catch((e) => { if (!initialBlogData()?.list?.length) setError(e.message); })
      .finally(() => setLoading(false));
  }, []);

  const featuredPost = posts.find(post => post.featured) || posts[0];
  const otherPosts = posts.filter(post => post !== featuredPost);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50">
      <SEO 
        title="Painting & Decorating Advice for Kent Homes | Paint It Better"
        description="Expert tips, cost guides and practical advice for painting and decorating projects in Kent. Helpful articles for homeowners and landlords."
        canonical="/blog"
      />
      {posts.length > 0 && <JsonLd data={blogListSchema(posts)} />}
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Painting & Decorating Tips for Kent Homeowners
            </h1>
            <p className="text-xl text-cyan-100">
              Expert advice, cost guides, and practical tips to help you plan your next decorating project.
            </p>
          </div>
        </div>
      </section>

      {loading && (
        <section className="py-16" data-testid="blog-loading">
          <div className="container mx-auto px-4 max-w-6xl grid md:grid-cols-3 gap-8">
            {[0, 1, 2].map(i => <Skeleton key={i} className="h-72 rounded-xl" />)}
          </div>
        </section>
      )}
      {error && (
        <section className="py-16">
          <p className="text-center text-red-600" data-testid="blog-error">Sorry, we couldn't load our articles right now. Please try again later.</p>
        </section>
      )}

      {/* Featured Post */}
      {featuredPost && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl font-bold text-slate-900 mb-8">Featured Article</h2>
              <Link to={`/blog/${featuredPost.slug}`} data-testid="blog-featured-card">
                <Card className="group overflow-hidden border-0 bg-white hover:shadow-2xl transition-all duration-300">
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="relative h-64 md:h-auto overflow-hidden">
                      <img 
                        src={featuredPost.cover_image_url} 
                        alt={`${featuredPost.title} – painting and decorating advice`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-cyan-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                          {featuredPost.category}
                        </span>
                      </div>
                    </div>
                    <CardContent className="p-8 md:p-12 flex flex-col justify-center">
                      <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 group-hover:text-cyan-600 transition-colors">
                        {featuredPost.title}
                      </h3>
                      <p className="text-slate-600 mb-6 leading-relaxed">
                        {featuredPost.excerpt}
                      </p>
                      <div className="flex items-center gap-6 text-sm text-slate-500 mb-6">
                        <span className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {formatPostDate(featuredPost.published_at)}
                        </span>
                        <span className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          {featuredPost.read_time}
                        </span>
                      </div>
                      <span className="inline-flex items-center text-cyan-600 font-semibold group-hover:translate-x-2 transition-transform">
                        Read Article
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </span>
                    </CardContent>
                  </div>
                </Card>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* All Posts */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">All Articles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}>
                  <Card className="group h-full overflow-hidden border-0 bg-white hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={post.cover_image_url} 
                        alt={`${post.title} – painting and decorating advice`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-cyan-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-cyan-600 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatPostDate(post.published_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {post.read_time}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Checkatrade CTA */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg text-slate-700 mb-4">
              Want to see real examples of our work and read what customers say? Visit{' '}
              <a 
                href="https://www.checkatrade.com/trades/PaintItBetterLtd" 
                target="_blank" 
                rel="noopener"
                className="text-cyan-600 hover:text-cyan-700 font-semibold underline decoration-2 underline-offset-2"
              >
                our Checkatrade profile
              </a>{' '}
              for verified reviews and project photos from Kent homeowners.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 to-teal-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl mb-8 text-cyan-100">
              Get in touch for a free, no-obligation quote on your painting and decorating project in Kent.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-cyan-600 hover:bg-cyan-50 px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-xl">
                Get Your Free Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Review Carousel */}
      <ReviewCarousel />
    </div>
  );
};

export default Blog;
